import { graphql, useStaticQuery } from 'gatsby';
import groq from 'groq';
import React, { useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import PreviewLoadingScreen from '../../preview/PreviewLoadingScreen';
import { usePreviewData } from '../../preview/previewUtils';
import { PageDocument } from '../../types/types';
import * as styles from './Calendar.module.scss';
import CalendlyCalendar from './CalendlyCalendar';
import HubSpotCalendar from './HubSpotCalendar';
import OnceHubCalendar from './OnceHubCalendar';

export interface CalendarProps {
  pageToGoAfterSubmit?: PageDocument;
  calendarId?: string;
  calendarLink?: string;
  hubspotCalendarId?: string;
  calendarType?: 'onceHub' | 'calendly' | 'hubspot';
  className?: string;
}

interface QueryData {
  sanityHpWebsiteSettings: {
    defaultScheduleOnceCalendarId: string;
    defaultCalendlyCalendarLink: string;
    defaultHubspotCalendarId: string;
  };
}

function Calendar(props: CalendarProps): React.ReactElement {
  const {
    pageToGoAfterSubmit,
    calendarId,
    calendarLink,
    hubspotCalendarId,
    calendarType = 'onceHub',
  } = props;
  const [isLoading, setIsLoading] = useState(true);

  const staticData = useStaticQuery<QueryData>(graphql`
    {
      sanityHpWebsiteSettings {
        defaultScheduleOnceCalendarId
        defaultCalendlyCalendarLink
        defaultHubspotCalendarId
      }
    }
  `);

  const groqQuery = groq`{
    "sanityHpWebsiteSettings": *[_id == "hp-website-settings-singleton"][0] {
      defaultScheduleOnceCalendarId,
      defaultCalendlyCalendarLink,
      defaultHubspotCalendarId
    }
  }`;

  const data = usePreviewData<QueryData>(staticData, {
    groqQuery,
  });

  if (!data) {
    return <PreviewLoadingScreen></PreviewLoadingScreen>;
  }

  return (
    <div className={styles.calendarContainer}>
      {isLoading && (
        <div className={styles.loadingCalendar}>
          <FaSpinner className={styles.loadingIcon} />
          <span className={styles.loadingLabel}>Loading calendar...</span>
        </div>
      )}

      {calendarType === 'hubspot' ? (
        <HubSpotCalendar
          pageToGoAfterSubmit={pageToGoAfterSubmit}
          calendarId={hubspotCalendarId || data.sanityHpWebsiteSettings.defaultHubspotCalendarId}
          className={styles.calendar}
          onLoad={() => setIsLoading(false)}
        />
      ) : calendarType === 'calendly' ? (
        <CalendlyCalendar
          pageToGoAfterSubmit={pageToGoAfterSubmit}
          calendarLink={calendarLink || data.sanityHpWebsiteSettings.defaultCalendlyCalendarLink}
          className={styles.calendar}
          onLoad={() => {
            setIsLoading(false);
          }}
        />
      ) : (
        <OnceHubCalendar
          pageToGoAfterSubmit={pageToGoAfterSubmit}
          calendarId={calendarId || data.sanityHpWebsiteSettings.defaultScheduleOnceCalendarId}
          className={styles.calendar}
        />
      )}
    </div>
  );
}

export default Calendar;
